/**
 * ==========================================================================
 *  Login.jsx
 * ==========================================================================
 *  Die Anmelde-Seite. Wird von App.jsx angezeigt, solange kein User
 *  eingeloggt ist (außer für die öffentlich erreichbare Widget-URL).
 *
 *  Design:
 *    - Hintergrundbild (falls /hintergrund.jpg im public/-Ordner liegt)
 *    - Sonst dezenter CI-Farbverlauf als Fallback
 *    - Weißes Login-Kästchen mittig mit sanftem Schatten
 *    - Passwort-vergessen-Link unter dem Formular
 * ==========================================================================
 */

import { useState } from 'react';
import { Loader2, LogIn, Mail, Lock, AlertCircle, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { api, ApiError } from '../api';

const CI = { brombeer: '#85366C', orchidee: '#DC91D0' };

export default function Login() {
  const { login, login2fa } = useAuth();

  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [fehler, setFehler] = useState('');
  const [laedt, setLaedt] = useState(false);

  // Zwei-Faktor-Schritt (erscheint nur, wenn der Account 2FA aktiv hat)
  const [twofaStep, setTwofaStep] = useState(false);
  const [code, setCode] = useState('');

  // Passwort-vergessen-Panel
  const [vergessenOffen, setVergessenOffen] = useState(false);
  const [vergessenMail, setVergessenMail] = useState('');
  const [vergessenGesendet, setVergessenGesendet] = useState(false);
  const [vergessenLaedt, setVergessenLaedt] = useState(false);

  async function handleLogin(e) {
    e.preventDefault();
    setFehler('');
    setLaedt(true);
    try {
      const r = await login(email.trim().toLowerCase(), passwort);
      if (r && r.twofaRequired) {
        setTwofaStep(true);   // zweiter Schritt: Authenticator-Code
      }
      // Sonst: eingeloggt, App.jsx wechselt automatisch aufs Dashboard
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setFehler('E-Mail oder Passwort ist falsch.');
      } else {
        setFehler('Anmeldung gerade nicht möglich. Bitte später nochmal.');
      }
    } finally {
      setLaedt(false);
    }
  }

  async function handle2fa(e) {
    e.preventDefault();
    setFehler('');
    setLaedt(true);
    try {
      await login2fa(code.trim());
      // Erfolg: App.jsx wechselt automatisch aufs Dashboard
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setFehler('Code ist falsch oder abgelaufen. Bitte den aktuellen Code aus der App eingeben.');
      } else {
        setFehler('Anmeldung gerade nicht möglich. Bitte später nochmal.');
      }
    } finally {
      setLaedt(false);
    }
  }

  function zurueckZuLogin() {
    setTwofaStep(false);
    setCode('');
    setPasswort('');
    setFehler('');
  }

  async function handlePasswortVergessen(e) {
    e.preventDefault();
    setVergessenLaedt(true);
    try {
      await api.post('/api/auth/passwort-vergessen', { email: vergessenMail.trim().toLowerCase() });
      setVergessenGesendet(true);
    } catch {
      // Bewusst keine Fehlermeldung — siehe User-Enumeration-Schutz im Backend
      setVergessenGesendet(true);
    } finally {
      setVergessenLaedt(false);
    }
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        // Hintergrundbild falls vorhanden (public/hintergrund.jpg ablegen)
        backgroundImage: `url('/hintergrund.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      {/* Fallback-Verlauf (wird durch Hintergrundbild überdeckt, wenn vorhanden) */}
      <div
        className="absolute inset-0"
        style={{ background: `linear-gradient(135deg, ${CI.brombeer} 0%, ${CI.orchidee} 100%)`, zIndex: -1 }}
      />
      {/* Halbtransparentes Overlay für bessere Lesbarkeit (wenn Foto) */}
      <div className="absolute inset-0 bg-slate-900/25" />

      {/* Login-Kästchen */}
      <div className="relative w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="p-8 text-center border-b border-gray-100" style={{ backgroundColor: CI.brombeer }}>
            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center mx-auto mb-3">
              <LogIn className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white">Beratungs-Cockpit</h1>
            <p className="text-white/80 text-sm mt-1">Pflege-Assistent Cloud</p>
          </div>

          {/* Inhalt — Login-Form, 2FA-Code-Schritt, oder Passwort-vergessen */}
          <div className="p-8">
            {twofaStep ? (
              <form onSubmit={handle2fa} className="space-y-4">
                <div className="text-center">
                  <div className="w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center" style={{ backgroundColor: '#F3E8F1' }}>
                    <ShieldCheck className="w-6 h-6" style={{ color: CI.brombeer }} />
                  </div>
                  <h2 className="text-lg font-bold text-gray-800">Bestätigung</h2>
                  <p className="text-sm text-gray-600 mt-1">
                    Gib den 6-stelligen Code aus deiner Authenticator-App ein.
                  </p>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  autoFocus
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full py-3 text-center tracking-[0.4em] text-xl font-bold border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent"
                  style={{ '--tw-ring-color': CI.brombeer }}
                />
                {fehler && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{fehler}</span>
                  </div>
                )}
                <button
                  type="submit"
                  disabled={laedt || code.length < 6}
                  className="w-full py-3 rounded-lg font-bold text-white shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 flex items-center justify-center gap-2"
                  style={{ backgroundColor: CI.brombeer }}
                >
                  {laedt ? (
                    <><Loader2 className="w-4 h-4 animate-spin" /> Prüfe…</>
                  ) : (
                    <><ShieldCheck className="w-4 h-4" /> Bestätigen</>
                  )}
                </button>
                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={zurueckZuLogin}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Zurück
                  </button>
                </div>
              </form>
            ) : !vergessenOffen ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    E-Mail
                  </label>
                  <div className="relative">
                    <Mail className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
                    <input
                      type="email"
                      required
                      autoComplete="username"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="beispiel@domain.de"
                      className="w-full py-3 pl-9 pr-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': CI.brombeer }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1">
                    Passwort
                  </label>
                  <div className="relative">
                    <Lock className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
                    <input
                      type="password"
                      required
                      autoComplete="current-password"
                      value={passwort}
                      onChange={(e) => setPasswort(e.target.value)}
                      placeholder="••••••••"
                      className="w-full py-3 pl-9 pr-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                      style={{ '--tw-ring-color': CI.brombeer }}
                    />
                  </div>
                </div>

                {fehler && (
                  <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                    <span>{fehler}</span>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={laedt || !email || !passwort}
                  className="w-full py-3 rounded-lg font-bold text-white shadow-md transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:translate-y-0 flex items-center justify-center gap-2"
                  style={{ backgroundColor: CI.brombeer }}
                >
                  {laedt ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Melde an…
                    </>
                  ) : (
                    <>
                      <LogIn className="w-4 h-4" />
                      Anmelden
                    </>
                  )}
                </button>

                <div className="text-center pt-2">
                  <button
                    type="button"
                    onClick={() => { setVergessenOffen(true); setVergessenMail(email); }}
                    className="text-xs text-gray-500 hover:text-gray-700 underline"
                  >
                    Passwort vergessen?
                  </button>
                </div>
              </form>
            ) : (
              /* Passwort-Vergessen-Formular */
              <div className="space-y-4">
                {!vergessenGesendet ? (
                  <form onSubmit={handlePasswortVergessen} className="space-y-4">
                    <h2 className="text-lg font-bold text-gray-800">Passwort zurücksetzen</h2>
                    <p className="text-sm text-gray-600">
                      Geben Sie Ihre E-Mail-Adresse ein. Wir schicken Ihnen einen Link zum Neusetzen
                      des Passworts (1 Stunde gültig).
                    </p>
                    <div className="relative">
                      <Mail className="w-4 h-4 absolute left-3 top-3.5 text-gray-400" />
                      <input
                        type="email"
                        required
                        autoComplete="email"
                        value={vergessenMail}
                        onChange={(e) => setVergessenMail(e.target.value)}
                        placeholder="beispiel@domain.de"
                        className="w-full py-3 pl-9 pr-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:border-transparent"
                        style={{ '--tw-ring-color': CI.brombeer }}
                      />
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setVergessenOffen(false)}
                        className="flex-1 py-3 rounded-lg font-bold text-gray-600 bg-gray-100 hover:bg-gray-200 transition-colors"
                      >
                        Zurück
                      </button>
                      <button
                        type="submit"
                        disabled={vergessenLaedt || !vergessenMail}
                        className="flex-1 py-3 rounded-lg font-bold text-white shadow-md hover:-translate-y-0.5 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                        style={{ backgroundColor: CI.brombeer }}
                      >
                        {vergessenLaedt ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Senden'}
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="text-center py-4">
                    <div className="w-14 h-14 rounded-full bg-green-100 mx-auto mb-4 flex items-center justify-center">
                      <Mail className="w-7 h-7 text-green-600" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">E-Mail gesendet</h3>
                    <p className="text-sm text-gray-600 mb-6">
                      Wenn die E-Mail-Adresse bei uns bekannt ist, haben Sie einen Link
                      zum Zurücksetzen erhalten. Schauen Sie auch in den Spam-Ordner.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setVergessenOffen(false); setVergessenGesendet(false); }}
                      className="py-2 px-6 rounded-lg font-bold text-white"
                      style={{ backgroundColor: CI.brombeer }}
                    >
                      Zurück zum Login
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Footer unter dem Kästchen */}
        <div className="text-center mt-6 text-xs text-white/80 drop-shadow">
          © {new Date().getFullYear()} Pflege-Assistent Cloud
        </div>
      </div>
    </div>
  );
}
